import { useState, useRef, useLayoutEffect, useCallback } from "react";

/**
 * trailGeometry — the shared geometry of the catalogue's path.
 *
 * Both the cobblestone path (CobbleTrail) and the meadow (FlowerTurf) need the
 * SAME serpentine: the cobbles fill inside it, the flowers keep clear of it. So
 * the measuring + centerline + spatial index live here and are computed ONCE
 * (via useTrailGeometry, in GameTypesGrid) and fed to both — they can never drift
 * apart, and the heavy fit runs a single time per layout change.
 */

// Shared deterministic seed; consumers pass distinct salts to hash01 for
// independent random streams (cobbles use 1–3, the flower scatter higher salts).
const SEED = 0x5f3c;
const ARC_SEGS = 6; // line segments used to round each 90° bend

// ---- deterministic positional hash -----------------------------------------
// A function of the grid position (not a sequential PRNG) so neighbouring cells
// agree on a shared corner — the cobbles rely on that to tessellate gap-free, and
// the flower scatter uses it for a stable per-cell placement.
export function hash01(ix, iy, salt) {
  let h = (SEED ^ salt) >>> 0;
  h = Math.imul(h ^ (ix + 0x9e3779b9), 0x85ebca6b);
  h = Math.imul(h ^ (iy + 0x165667b1), 0xc2b2ae35);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296; // [0, 1)
}

// mulberry32 — tiny deterministic sequential PRNG (the counterpart to hash01:
// use this when a consumer wants a SEQUENCE from a seed — e.g. baking one
// flower template or one category's prop scatter — and hash01 when it needs
// position-stable per-cell values).
export function mulberry32(seed) {
  return function () {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ---- centerline -------------------------------------------------------------
// How far the path's outer edge pulls in from the catalogue edges. Zero on
// phones/tablets (the U runs edge-to-edge as before); on wide screens it ramps up
// so a margin of meadow can grow around the outside of the U. Derived from the
// width alone, so it's stable across re-measures (content reflow can't change it).
export function trailEdgeInset(width) {
  const START = 1080; // below this the path still reaches the catalogue edges
  const MAX = 40; //     most it ever pulls in on each side
  return Math.max(0, Math.min((width - START) * 0.1, MAX));
}

// Reconstruct the serpentine the old per-row bars drew, centered in the W-wide
// corridor: entry stub -> across the first row's top edge -> down each gutter /
// across each divider (alternating sides) -> exit stub. Even rows hug the left.
// `edge` insets both vertical sides of the U from the catalogue edges.
function buildCenterline(rows, W, width, edge) {
  const r = W / 2;
  const leftX = edge + r;
  const rightX = width - edge - r;
  const gutterX = (i) => (i % 2 === 0 ? leftX : rightX); // hugged side
  const openX = (i) => (i % 2 === 0 ? rightX : leftX); // opposite side
  const n = rows.length;
  const top0 = rows[0].top;
  const pts = [];

  // Entry stub on row 0's open side, then across the top edge into the gutter.
  pts.push([openX(0), top0 - W]); // entry cap (pokes above the first row)
  pts.push([openX(0), top0 + r]); // turn: down -> left
  pts.push([gutterX(0), top0 + r]); // turn: left -> down (into the gutter)

  // Each row: down its gutter to the divider, then across to the far side. The
  // far side is the next row's gutter (alternates), so the join is a straight
  // vertical continuation — the corner sits at the divider.
  for (let i = 0; i < n; i++) {
    const bottom = rows[i].top + rows[i].height;
    pts.push([gutterX(i), bottom - r]); // down the gutter
    pts.push([openX(i), bottom - r]); // across the divider
  }

  const lastBottom = rows[n - 1].top + rows[n - 1].height;
  pts.push([openX(n - 1), lastBottom + W]); // exit cap (drops below the last row)
  return pts;
}

// Round every interior corner of the polyline into ARC_SEGS short segments (a
// quadratic fillet through the corner — close enough to a quarter circle for both
// the stroked mortar base and the distance test). Returns a flat point list.
function flattenRounded(pts, r) {
  if (pts.length < 3) return pts.slice();
  const out = [pts[0]];
  for (let i = 1; i < pts.length - 1; i++) {
    const p0 = pts[i - 1];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const d1 = Math.hypot(p0[0] - p1[0], p0[1] - p1[1]);
    const d2 = Math.hypot(p2[0] - p1[0], p2[1] - p1[1]);
    const rr = Math.min(r, d1 / 2, d2 / 2);
    const a = [p1[0] + ((p0[0] - p1[0]) / d1) * rr, p1[1] + ((p0[1] - p1[1]) / d1) * rr];
    const b = [p1[0] + ((p2[0] - p1[0]) / d2) * rr, p1[1] + ((p2[1] - p1[1]) / d2) * rr];
    out.push(a);
    for (let s = 1; s < ARC_SEGS; s++) {
      const t = s / ARC_SEGS;
      const u = 1 - t;
      // quadratic bezier a -> (control p1) -> b
      out.push([
        u * u * a[0] + 2 * u * t * p1[0] + t * t * b[0],
        u * u * a[1] + 2 * u * t * p1[1] + t * t * b[1],
      ]);
    }
    out.push(b);
  }
  out.push(pts[pts.length - 1]);
  return out;
}

// Squared distance from a point to a segment.
function segDist2(px, py, ax, ay, bx, by) {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  let t = len2 ? ((px - ax) * dx + (py - ay) * dy) / len2 : 0;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  const cx = ax + t * dx;
  const cy = ay + t * dy;
  return (px - cx) * (px - cx) + (py - cy) * (py - cy);
}

// Spatial index over the centerline segments so a distance query touches only
// nearby segments, not all of them. Each segment is filed in every grid cell its
// bounding box covers; cell size = W, so any segment within W of a point lives in
// that point's 3x3 cell neighbourhood. A FLAT array (not a Map) keeps each query
// to a few array reads — both consumers walk tens of thousands of cells.
function buildSegIndex(line, bucket, minX, minY, cols, rows) {
  const grid = new Array(cols * rows);
  for (let i = 0; i < line.length - 1; i++) {
    const ax = line[i][0];
    const ay = line[i][1];
    const bx = line[i + 1][0];
    const by = line[i + 1][1];
    const gx0 = Math.floor((Math.min(ax, bx) - minX) / bucket);
    const gx1 = Math.floor((Math.max(ax, bx) - minX) / bucket);
    const gy0 = Math.floor((Math.min(ay, by) - minY) / bucket);
    const gy1 = Math.floor((Math.max(ay, by) - minY) / bucket);
    for (let gx = gx0; gx <= gx1; gx++) {
      for (let gy = gy0; gy <= gy1; gy++) {
        const k = gy * cols + gx;
        const arr = grid[k];
        if (arr) arr.push(i);
        else grid[k] = [i];
      }
    }
  }
  return { grid, bucket, minX, minY, cols, rows };
}

// Min squared distance from a point to the centerline, via the segment index.
// A point OUTSIDE the indexed grid is provably > W from the path (the grid is
// padded a full W beyond it), so its empty/out-of-bounds cells return Infinity —
// which both consumers read correctly as "far from the path".
export function distToLine2(px, py, line, index) {
  const { grid, bucket, minX, minY, cols, rows } = index;
  const gx = Math.floor((px - minX) / bucket);
  const gy = Math.floor((py - minY) / bucket);
  let best = Infinity;
  for (let dy = -1; dy <= 1; dy++) {
    const ry = gy + dy;
    if (ry < 0 || ry >= rows) continue;
    for (let dx = -1; dx <= 1; dx++) {
      const rx = gx + dx;
      if (rx < 0 || rx >= cols) continue;
      const arr = grid[ry * cols + rx];
      if (!arr) continue;
      for (let n = 0; n < arr.length; n++) {
        const i = arr[n];
        const d = segDist2(px, py, line[i][0], line[i][1], line[i + 1][0], line[i + 1][1]);
        if (d < best) best = d;
      }
    }
  }
  return best;
}

// ---- measure + assemble -----------------------------------------------------
// Cheap DOM read: the row rects + path width, plus a key summarising them. Split
// from the math so we can skip the (heavier) fit whenever the geometry is unchanged
// — ResizeObserver fires on any reflow, but the trail only depends on these numbers.
function measureRows(container) {
  const beds = Array.from(container.querySelectorAll(":scope > .garden-bed"));
  if (!beds.length) return null;
  // The corridor width W is `--trail-w`, which a custom property won't resolve to
  // px for us — but .garden-bed's padding-bottom is set to it, and THAT resolves.
  const W = parseFloat(getComputedStyle(beds[0]).paddingBottom);
  const width = container.clientWidth;
  if (!W || !width) return null;
  const rows = beds.map((b) => ({ top: b.offsetTop, height: b.offsetHeight }));
  const edge = trailEdgeInset(width);
  const key = `${W}|${width}|${edge}|${rows.map((r) => `${r.top},${r.height}`).join(";")}`;
  return { W, width, edge, rows, key };
}

// The math: turn measured rows into the shared geometry — the rounded centerline
// polyline, the mortar path string, the path's vertical extent, and the segment
// index (built once here, used by both the cobble fit and the flower rejection).
// Exported so tests can build geometry from synthetic rows (jsdom has no layout,
// so measureRows can't run there — the pure math is fed rows directly).
export function computeGeometry({ W, width, rows, edge = 0 }) {
  const corners = buildCenterline(rows, W, width, edge);
  const line = flattenRounded(corners, W / 2);
  const mortarD =
    "M" + line.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join("L");

  const minY = rows[0].top - W;
  const maxY = rows[rows.length - 1].top + rows[rows.length - 1].height + W;

  // Index padded one path-width beyond the path's extent. Sized to the PATH (not
  // the meadow bleed) on purpose: any query outside it is > W from the path and so
  // returns Infinity, which is exactly "clear of the path" for the meadow margins.
  const bucket = W;
  const gMinX = -bucket;
  const gMinY = minY - bucket;
  const gCols = Math.ceil((width - gMinX) / bucket) + 2;
  const gRows = Math.ceil((maxY - gMinY) / bucket) + 2;
  const index = buildSegIndex(line, bucket, gMinX, gMinY, gCols, gRows);

  return { W, width, edge, rows, line, index, minY, maxY, mortarD };
}

/**
 * Measure the .garden-beds container and return the shared trail geometry,
 * recomputed only when the row geometry actually changes.
 *
 * @param {React.RefObject} containerRef  ref to the .garden-beds container
 * @param {string|number} signature       bump to force a re-measure (rows added /
 *                                         games loaded change row heights)
 * @returns the geometry object (stable identity until the layout changes), or null
 */
export function useTrailGeometry(containerRef, signature) {
  const [geometry, setGeometry] = useState(null);
  const rafRef = useRef(0);
  const keyRef = useRef(null); // geometry of the last fit — skip recompute if unchanged

  const measure = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const m = measureRows(el);
    if (!m || m.key === keyRef.current) return; // nothing (relevant) changed
    keyRef.current = m.key;
    setGeometry(computeGeometry(m));
  }, [containerRef]);

  useLayoutEffect(() => {
    measure();
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const schedule = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(measure);
    };
    const ro = new ResizeObserver(schedule);
    ro.observe(el); // container height changes as categories load
    el.querySelectorAll(":scope > .garden-bed").forEach((b) => ro.observe(b));
    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, [measure, signature, containerRef]);

  return geometry;
}
